import { BitMask64Utils } from '../Utils/BigIntCompatibility';
import { SoAStorage } from './SoAStorage';
import { createLogger } from '../../Utils/Logger';
import { getComponentTypeName } from '../Decorators';
import { ComponentRegistry, GlobalComponentRegistry } from './ComponentStorage/ComponentRegistry';
// 导出核心类型
// Export core types
export { ComponentRegistry, GlobalComponentRegistry };
/**
 * 高性能组件存储器
 */
export class ComponentStorage {
    constructor(componentType) {
        this.dense = [];
        this.entityIds = [];
        this.entityToIndex = new Map();
        this.componentType = componentType;
    }
    /**
     * 添加组件
     * @param entityId 实体ID
     * @param component 组件实例
     */
    addComponent(entityId, component) {
        // 检查实体是否已有此组件
        if (this.entityToIndex.has(entityId)) {
            throw new Error(`Entity ${entityId} already has component ${getComponentTypeName(this.componentType)}`);
        }
        // 末尾插入到致密数组
        const index = this.dense.length;
        this.dense.push(component);
        this.entityIds.push(entityId);
        this.entityToIndex.set(entityId, index);
    }
    /**
     * 获取组件
     * @param entityId 实体ID
     * @returns 组件实例或null
     */
    getComponent(entityId) {
        const index = this.entityToIndex.get(entityId);
        return index !== undefined ? this.dense[index] : null;
    }
    /**
     * 检查实体是否有此组件
     * @param entityId 实体ID
     * @returns 是否有组件
     */
    hasComponent(entityId) {
        return this.entityToIndex.has(entityId);
    }
    /**
     * 移除组件
     * @param entityId 实体ID
     * @returns 被移除的组件或null
     */
    removeComponent(entityId) {
        const index = this.entityToIndex.get(entityId);
        if (index === undefined) {
            return null;
        }
        const component = this.dense[index];
        const lastIndex = this.dense.length - 1;
        if (index !== lastIndex) {
            // 将末尾元素交换到要删除的位置
            const lastComponent = this.dense[lastIndex];
            const lastEntityId = this.entityIds[lastIndex];
            this.dense[index] = lastComponent;
            this.entityIds[index] = lastEntityId;
            // 更新被交换元素的映射
            this.entityToIndex.set(lastEntityId, index);
        }
        // 移除末尾元素
        this.dense.pop();
        this.entityIds.pop();
        this.entityToIndex.delete(entityId);
        return component;
    }
    /**
     * 高效遍历所有组件
     * @param callback 回调函数
     */
    forEach(callback) {
        for (let i = 0; i < this.dense.length; i++) {
            callback(this.dense[i], this.entityIds[i], i);
        }
    }
    /**
     * 获取所有组件
     * @returns 组件数组
     */
    getDenseArray() {
        return {
            components: [...this.dense],
            entityIds: [...this.entityIds]
        };
    }
    /**
     * 清空所有组件
     */
    clear() {
        this.dense.length = 0;
        this.entityIds.length = 0;
        this.entityToIndex.clear();
    }
    /**
     * 获取组件数量
     */
    get size() {
        return this.dense.length;
    }
    /**
     * 获取组件类型
     */
    get type() {
        return this.componentType;
    }
    /**
     * 获取存储统计信息
     */
    getStats() {
        const totalSlots = this.dense.length;
        const usedSlots = this.dense.length;
        const freeSlots = 0; // 永远无空洞
        const fragmentation = 0; // 永远无碎片
        return {
            totalSlots,
            usedSlots,
            freeSlots,
            fragmentation
        };
    }
}
/**
 * 组件存储管理器
 * 管理所有组件类型的存储器
 */
export class ComponentStorageManager {
    constructor() {
        this.storages = new Map();
    }
    /**
     * 检查组件类型是否启用SoA存储
     * @param componentType 组件类型
     * @returns 是否为SoA存储
     */
    isSoAStorage(componentType) {
        const storage = this.storages.get(componentType);
        return storage instanceof SoAStorage;
    }
    /**
     * 获取SoA存储器（类型安全）
     * @param componentType 组件类型
     * @returns SoA存储器或null
     */
    getSoAStorage(componentType) {
        const storage = this.getStorage(componentType);
        return storage instanceof SoAStorage ? storage : null;
    }
    /**
     * 直接获取SoA字段数组（类型安全）
     * @param componentType 组件类型
     * @param fieldName 字段名
     * @returns TypedArray或null
     */
    getFieldArray(componentType, fieldName) {
        const soaStorage = this.getSoAStorage(componentType);
        return soaStorage ? soaStorage.getFieldArray(fieldName) : null;
    }
    /**
     * 直接获取SoA字段数组（类型安全，带字段名检查）
     * @param componentType 组件类型
     * @param fieldName 字段名（类型检查）
     * @returns TypedArray或null
     */
    getTypedFieldArray(componentType, fieldName) {
        const soaStorage = this.getSoAStorage(componentType);
        return soaStorage ? soaStorage.getTypedFieldArray(fieldName) : null;
    }
    /**
     * 获取SoA存储的活跃索引
     * @param componentType 组件类型
     * @returns 活跃索引数组或空数组
     */
    getActiveIndices(componentType) {
        const soaStorage = this.getSoAStorage(componentType);
        return soaStorage ? soaStorage.getActiveIndices() : [];
    }
    /**
     * 获取实体在SoA存储中的索引
     * @param componentType 组件类型
     * @param entityId 实体ID
     * @returns 存储索引或undefined
     */
    getEntityIndex(componentType, entityId) {
        const soaStorage = this.getSoAStorage(componentType);
        return soaStorage ? soaStorage.getEntityIndex(entityId) : undefined;
    }
    /**
     * 根据索引获取实体ID
     * @param componentType 组件类型
     * @param index 存储索引
     * @returns 实体ID或undefined
     */
    getEntityIdByIndex(componentType, index) {
        const soaStorage = this.getSoAStorage(componentType);
        return soaStorage ? soaStorage.getEntityIdByIndex(index) : undefined;
    }
    /**
     * 获取或创建组件存储器（默认原始存储）
     * @param componentType 组件类型
     * @returns 组件存储器
     */
    getStorage(componentType) {
        let storage = this.storages.get(componentType);
        if (!storage) {
            // 检查是否启用SoA优化
            const enableSoA = componentType.__enableSoA;
            if (enableSoA) {
                // 使用SoA优化存储
                storage = new SoAStorage(componentType);
                ComponentStorageManager._logger.info(`为 ${getComponentTypeName(componentType)} 启用SoA优化（适用于大规模批量操作）`);
            }
            else {
                // 默认使用原始存储
                storage = new ComponentStorage(componentType);
            }
            this.storages.set(componentType, storage);
        }
        return storage;
    }
    /**
     * 添加组件
     * @param entityId 实体ID
     * @param component 组件实例
     */
    addComponent(entityId, component) {
        const componentType = component.constructor;
        const storage = this.getStorage(componentType);
        storage.addComponent(entityId, component);
    }
    /**
     * 获取组件
     * @param entityId 实体ID
     * @param componentType 组件类型
     * @returns 组件实例或null
     */
    getComponent(entityId, componentType) {
        const storage = this.storages.get(componentType);
        return storage ? storage.getComponent(entityId) : null;
    }
    /**
     * 检查实体是否有组件
     * @param entityId 实体ID
     * @param componentType 组件类型
     * @returns 是否有组件
     */
    hasComponent(entityId, componentType) {
        const storage = this.storages.get(componentType);
        return storage ? storage.hasComponent(entityId) : false;
    }
    /**
     * 移除组件
     * @param entityId 实体ID
     * @param componentType 组件类型
     * @returns 被移除的组件或null
     */
    removeComponent(entityId, componentType) {
        const storage = this.storages.get(componentType);
        return storage ? storage.removeComponent(entityId) : null;
    }
    /**
     * 移除实体的所有组件
     * @param entityId 实体ID
     */
    removeAllComponents(entityId) {
        for (const storage of this.storages.values()) {
            storage.removeComponent(entityId);
        }
    }
    /**
     * 获取实体的组件位掩码
     * Get component bitmask for entity
     *
     * @param entityId 实体ID | Entity ID
     * @param registry 组件注册表（可选，默认使用全局注册表）| Component registry (optional, defaults to global)
     * @returns 组件位掩码 | Component bitmask
     */
    getComponentMask(entityId, registry = GlobalComponentRegistry) {
        const mask = BitMask64Utils.clone(BitMask64Utils.ZERO);
        for (const [componentType, storage] of this.storages.entries()) {
            if (storage.hasComponent(entityId)) {
                const componentMask = registry.getBitMask(componentType);
                BitMask64Utils.orInPlace(mask, componentMask);
            }
        }
        return mask;
    }
    /**
     * 获取所有存储器的统计信息
     */
    getAllStats() {
        const stats = new Map();
        for (const [componentType, storage] of this.storages.entries()) {
            const typeName = getComponentTypeName(componentType);
            stats.set(typeName, storage.getStats());
        }
        return stats;
    }
    /**
     * 清空所有存储器
     */
    clear() {
        for (const storage of this.storages.values()) {
            storage.clear();
        }
        this.storages.clear();
    }
}
ComponentStorageManager._logger = createLogger('ComponentStorage');
//# sourceMappingURL=ComponentStorage.js.map